/**
 * Behaviour every page gets — the counterpart of the .in engine's
 * js/@core/index.js. Each page bundle calls grabOnInit() first, then adds its
 * own page behaviour.
 *
 * The rule for everything in here: the page must already work without it. The
 * markup is complete and indexable at generation time, so @core only enhances
 * — nothing in it may render content.
 *
 * Not ported from .in (India-only, or with no International equivalent yet):
 * the notifications panel, push notifications, CID and user-navigation
 * tracking, ads tracking, add-to-home-screen tracking.
 */
import { goRedirectionInit } from '@common/events/go-redirection';
import { couponClickInit } from '@common/events/coupon-click';
import { lazyLoadInit } from '@common/events/lazyload';
import { goBackToTopButtonInit } from '@common/events/go-back-to-top';
import { headerInit } from '@common/headers/header';
import { searchInit } from '@common/headers/search';
import { emailSubscriptionInit } from '@common/headers/email-sub';
import { bannerInit, promoBannerInit } from '@common/headers/banners';
import { loginInit } from '@common/headers/login';
import { commonGATrackingInit } from '@common/tracking/common-ga-tracking';
import { firebasePushInit } from '@common/events/push-notification';

export function grabOnInit() {
  headerInit();
  commonGATrackingInit();
  searchInit();
  // Every page has the header, so every page needs the profile chip to render
  // for a signed-in visitor — not just the ones that open the popup.
  loginInit();
  goRedirectionInit();
  couponClickInit();
  lazyLoadInit();
  goBackToTopButtonInit();
  emailSubscriptionInit();
  // Server-rendered, no fetch — placed before the bell so a slow bell request
  // never delays it.
  promoBannerInit();
  // The bell fetches its own banners, so it runs last and never blocks the
  // behaviour above.
  bannerInit();

  // Web push — deferred to idle exactly as the .in core defers its own
  // (the SDK chunk, the SW registration and the permission prompt are heavy
  // and non-urgent); the flow itself is the International app's. Off unless
  // goGlobal.FIREBASE is configured, and never allowed to break the page.
  const deferToIdle = window.requestIdleCallback || ((cb) => window.setTimeout(cb, 2000));
  deferToIdle(
    () => {
      try {
        firebasePushInit();
      } catch {
        /* never let push init break the page */
      }
    },
    { timeout: 5000 },
  );

  return window.goGlobal || {};
}

/** Bundles are deferred, so this usually fires immediately. */
export function onReady(fn) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fn, { once: true });
  } else {
    fn();
  }
}
